import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Eye, FileText, Loader2, Mail, Paperclip, Plus, RefreshCw, Search } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { usePortalFocus, useScrollToFocus } from "@/hooks/usePortalFocus";
import {
  CLOSED_REQUEST_STATUSES,
  cleanEmailText,
  readableSize,
  FILE_REQUEST_STATUSES,
  groupRequestsByMonth,
  joinRequestNotes,
  letterToAttach,
  matchesRequestType,
  requestTypeOptions,
  splitRequestNotes,
} from "@/lib/fileRequests";
import {
  attachRequestLetter,
  createManualRequest,
  fetchFileRequests,
  fetchRequestDetail,
  requestAttachmentUrl,
  saveRequestNotes,
  saveRequestStatus,
} from "@/lib/fileRequestsClient";

const FIRM_NAMES = {
  lacw: "Law and Advocacy Centre for Women",
  vals: "Victorian Aboriginal Legal Service",
  rga: "Robyn Greensill & Associates",
  eas: "EAS Legal",
};

const STATUS_STYLES = {
  "": "border-sky-200 bg-sky-50 text-sky-800",
  waiting_to_be_submitted_in_atlas: "border-blue-200 bg-blue-50 text-blue-800",
  pending_in_atlas: "border-amber-200 bg-amber-50 text-amber-900",
  aid_submitted: "border-violet-200 bg-violet-50 text-violet-800",
  aid_approved: "border-emerald-200 bg-emerald-50 text-emerald-800",
  aid_not_submitted: "border-rose-200 bg-rose-50 text-rose-800",
};

function formatDate(value) {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime())
    ? date.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })
    : "";
}

/**
 * New legal aid files for a firm, from the request email to VLA's decision. The same requests
 * the Aid Planner shows beside its matters (src/lib/fileRequests.js); this page replaces the
 * stand-alone Legal Aid Status Monitor that used to be framed in here.
 */
export default function LegalAidStatusMonitor({ brand = "lacw" }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [emailFeedMissing, setEmailFeedMissing] = useState(false);
  const [filter, setFilter] = useState("open");
  // New file, grant status check, extension application, disbursement...: the boxes the
  // Client Account dashboard split these emails into.
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [openMonths, setOpenMonths] = useState({});
  const [adding, setAdding] = useState(false);
  // A request opened from the morning summary: its month is opened, it is scrolled to, and
  // its email opens.
  const focus = usePortalFocus("file-request", brand);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await fetchFileRequests(brand);
      setRequests(result.requests);
      setEmailFeedMissing(result.emailFeedMissing);
    } catch (err) {
      setError(err.message || "The file requests could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [brand]);

  useEffect(() => { load(); }, [load]);

  const update = (key, changes) => setRequests((current) => current.map((item) => (item.key === key ? { ...item, ...changes } : item)));

  const changeStatus = async (request, status) => {
    update(request.key, { status });
    try {
      await saveRequestStatus(request, status);
    } catch (err) {
      update(request.key, { status: request.status });
      toast.error(err.message || "The status could not be saved.");
    }
  };

  const changeNotes = async (request, notes) => {
    if (notes === request.notes) return;
    update(request.key, { notes });
    try {
      await saveRequestNotes(request, notes);
    } catch (err) {
      update(request.key, { notes: request.notes });
      toast.error(err.message || "The notes could not be saved.");
    }
  };

  const attachLetter = async (request, label, file) => {
    try {
      const notes = await attachRequestLetter(request, label, file);
      update(request.key, { notes });
      toast.success(`${label} attached for ${request.clientName}.`);
    } catch (err) {
      toast.error(err.message || `The ${label.toLowerCase()} could not be attached.`);
    }
  };

  const counts = useMemo(() => {
    const byStatus = Object.fromEntries(FILE_REQUEST_STATUSES.map((option) => [option.value, 0]));
    for (const request of requests) byStatus[request.status] = (byStatus[request.status] || 0) + 1;
    return byStatus;
  }, [requests]);
  const openCount = requests.filter((request) => !CLOSED_REQUEST_STATUSES.includes(request.status)).length;

  const visible = useMemo(() => {
    const query = search.trim().toLowerCase();
    return requests.filter((request) => {
      if (filter === "open" && CLOSED_REQUEST_STATUSES.includes(request.status)) return false;
      if (filter !== "open" && filter !== "all" && request.status !== filter) return false;
      if (!matchesRequestType(request, typeFilter)) return false;
      return !query || `${request.clientName} ${request.subject} ${request.notes}`.toLowerCase().includes(query);
    });
  }, [requests, filter, typeFilter, search]);
  const typeOptions = useMemo(() => requestTypeOptions(requests), [requests]);

  const months = useMemo(() => groupRequestsByMonth(visible), [visible]);
  // The newest month starts open, the rest closed, until someone opens or closes one.
  const isOpen = (month, index) => openMonths[month.key] ?? index === 0;

  useEffect(() => {
    if (!focus || loading) return;
    const month = months.find((group) => group.requests.some((request) => request.key === focus.id));
    if (month) setOpenMonths((current) => (current[month.key] ? current : { ...current, [month.key]: true }));
    // Filtered out (decided since, or a search): show everything so it can be found.
    else if (requests.some((request) => request.key === focus.id)) {
      setFilter("all");
      setTypeFilter("all");
      setSearch("");
    }
  }, [focus, loading, months, requests]);
  useScrollToFocus(focus?.id, !loading);

  const filters = [
    { value: "open", label: "Open", count: openCount },
    ...FILE_REQUEST_STATUSES.map((option) => ({ value: option.value, label: option.label, count: counts[option.value] || 0 })),
    { value: "all", label: "All", count: requests.length },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-500" /> Legal Aid Status Monitor
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            {FIRM_NAMES[brand] ? `${FIRM_NAMES[brand]} – n` : "N"}ew file requests, from the request email to VLA's decision.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setAdding((open) => !open)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <Plus className="w-3.5 h-3.5" /> Add a request
          </button>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      {adding && <AddRequestForm brand={brand} onAdded={() => { setAdding(false); load(); }} />}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex flex-wrap gap-2">
          {filters.map((option) => (
            <button
              key={option.value || "new"}
              type="button"
              onClick={() => setFilter(option.value)}
              className={
                "rounded-lg border px-3 py-1.5 text-xs font-semibold transition " +
                (filter === option.value
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 text-slate-600 hover:bg-slate-50")
              }
            >
              {option.label}{option.count ? ` (${option.count})` : ""}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-slate-600">
          Type
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-lg border border-slate-200 bg-white py-1.5 px-2 text-xs"
          >
            <option value="all">All types ({requests.length})</option>
            {typeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.value} ({option.count})</option>
            ))}
          </select>
        </label>
        <label className="relative">
          <span className="sr-only">Search requests</span>
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search client, subject or notes"
            className="rounded-lg border border-slate-200 py-1.5 pl-8 pr-3 text-xs w-60"
          />
        </label>
        </div>
      </div>

      {emailFeedMissing && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-4 py-3 mb-4">
          The request emails couldn't be loaded just now, so only requests added by hand are shown. Try Refresh in a moment.
        </p>
      )}
      {error && (
        <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-4 py-3 mb-4">{error}</p>
      )}

      {loading && requests.length === 0 ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 py-12 justify-center">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading file requests…
        </div>
      ) : months.length === 0 ? (
        <div className="text-center py-16 text-sm text-slate-500 border border-dashed border-slate-200 rounded-xl">
          {filter === "open" && !search && typeFilter === "all" ? "No open file requests." : "No file requests match."}
        </div>
      ) : (
        <div className="space-y-3">
          {months.map((month, index) => {
            const open = isOpen(month, index);
            return (
              <section key={month.key} className="rounded-xl border border-slate-200 bg-white overflow-hidden">
                <button
                  type="button"
                  onClick={() => setOpenMonths((current) => ({ ...current, [month.key]: !open }))}
                  aria-expanded={open}
                  className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 text-left"
                >
                  <span className="text-sm font-semibold text-slate-800">
                    {month.label} <span className="font-normal text-slate-500">({month.requests.length})</span>
                  </span>
                  {open ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>
                {open && (
                  <ul className="divide-y divide-slate-100">
                    {month.requests.map((request) => (
                      <RequestRow
                        key={request.key}
                        request={request}
                        focused={request.key === focus?.id}
                        onStatus={changeStatus}
                        onNotes={changeNotes}
                        onAttach={attachLetter}
                      />
                    ))}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      )}

      {/* Only LACW and VALS have an Aid Planner. */}
      {(brand === "lacw" || brand === "vals") && (
        <p className="mt-6 text-xs text-slate-500">
          ATLAS letters are uploaded in the{" "}
          <a href={`/${brand}-aid-planner`} className="font-semibold text-slate-700 underline">Aid Planner</a>
          , which reads each letter, updates the client's matter and moves the matching request on here.
        </p>
      )}
    </div>
  );
}

function RequestRow({ request, focused = false, onStatus, onNotes, onAttach }) {
  const { text, attachments } = splitRequestNotes(request.notes);
  const [draft, setDraft] = useState(text);
  const fileInput = useRef(null);
  const [attaching, setAttaching] = useState(false);
  // Opened from the morning summary: straight to the email.
  const [viewing, setViewing] = useState(focused);
  const letter = letterToAttach(request);

  // A saved note (or a letter attached elsewhere) replaces what's in the box.
  useEffect(() => { setDraft(text); }, [text]);

  const saveNote = () => {
    if (draft.trim() === text) return;
    onNotes(request, joinRequestNotes(draft, attachments));
  };

  const attach = async (file) => {
    if (!file) return;
    setAttaching(true);
    await onAttach(request, letter, file);
    setAttaching(false);
  };

  return (
    <li data-focus-id={request.key} className="grid gap-4 px-4 py-4 md:grid-cols-[minmax(0,1.1fr)_minmax(0,1.6fr)_auto_minmax(0,1.4fr)] md:items-start">
      <div className="min-w-0">
        <p className="text-sm font-semibold text-slate-900 truncate">{request.clientName}</p>
        <p className="text-xs text-slate-500 mt-0.5">Received {formatDate(request.date) || "—"}</p>
        <div className="mt-1.5 flex flex-wrap gap-1">
          {request.urgent && (
            <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700">Urgent</span>
          )}
          {request.category && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{request.category}</span>
          )}
          {request.isManual && (
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">Added by hand</span>
          )}
        </div>
      </div>

      <div className="min-w-0">
        <p className="text-sm text-slate-800">{request.subject}</p>
        {request.snippet && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{request.snippet}</p>}
        <button
          type="button"
          onClick={() => setViewing(true)}
          className="mt-1.5 inline-flex items-center gap-1 text-xs font-semibold text-slate-600 hover:text-slate-900 hover:underline"
        >
          <Eye className="w-3.5 h-3.5" /> View full request
        </button>
        {viewing && <RequestDetailDialog request={request} onClose={() => setViewing(false)} />}
      </div>

      <div className="flex flex-col gap-2 md:w-48">
        <label className="sr-only" htmlFor={`status-${request.key}`}>Status for {request.clientName}</label>
        <select
          id={`status-${request.key}`}
          value={request.status}
          onChange={(e) => onStatus(request, e.target.value)}
          className={`rounded-full border px-3 py-1.5 text-xs font-semibold focus:outline-none focus:ring-2 focus:ring-slate-300 ${STATUS_STYLES[request.status] || STATUS_STYLES[""]}`}
        >
          {FILE_REQUEST_STATUSES.map((option) => (
            <option key={option.value || "new"} value={option.value}>{option.label}</option>
          ))}
        </select>
        {letter && (
          <>
            <input ref={fileInput} type="file" accept="application/pdf,image/*" className="hidden" onChange={(e) => { attach(e.target.files?.[0]); e.target.value = ""; }} />
            <button
              type="button"
              onClick={() => fileInput.current?.click()}
              disabled={attaching}
              className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-dashed border-slate-300 px-2 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
            >
              {attaching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Attach {letter.toLowerCase()}
            </button>
          </>
        )}
      </div>

      <div className="min-w-0 space-y-1.5">
        <label className="sr-only" htmlFor={`notes-${request.key}`}>Notes for {request.clientName}</label>
        <textarea
          id={`notes-${request.key}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={saveNote}
          rows={2}
          placeholder="Add a note…"
          className="w-full resize-y rounded-lg border border-slate-200 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-300"
        />
        {attachments.length > 0 && (
          <ul className="flex flex-wrap gap-1.5">
            {attachments.map((item, i) => (
              <li key={i}>
                <a
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-0.5 text-xs text-slate-700 hover:bg-slate-50"
                >
                  <FileText className="w-3 h-3 text-slate-400" /> {item.label}
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  );
}

// A file in the full request: shown as a button so it reads as something to open.
function FileLink({ href, name, size }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex items-center gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-800 hover:bg-blue-100 hover:border-blue-300 focus:outline-none focus:ring-2 focus:ring-blue-300"
    >
      <FileText className="w-4 h-4 shrink-0 text-blue-600" />
      <span className="min-w-0 flex-1 truncate font-medium underline-offset-2 group-hover:underline">{name}</span>
      {size ? <span className="shrink-0 text-xs text-blue-700/70">{size}</span> : null}
      <span className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold">
        Open <ExternalLink className="w-3.5 h-3.5" />
      </span>
    </a>
  );
}

// A later email in the request's thread: its date and subject, opened on request to its
// text and attachments.
function FollowUpEmail({ followUp }) {
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const asRequest = { messageId: followUp.messageId, isManual: false, snippet: followUp.snippet };

  useEffect(() => {
    if (!open || detail) return undefined;
    let current = true;
    fetchRequestDetail(asRequest)
      .then((result) => { if (current) setDetail(result); })
      .catch((err) => { if (current) setError(err.message || "The email couldn't be opened."); });
    return () => { current = false; };
    // asRequest is rebuilt each render from followUp, which is what the effect depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, detail, followUp.messageId]);

  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-xs"
      >
        <span className="min-w-0 truncate text-slate-700">
          <span className="font-semibold">{formatDate(followUp.date) || "—"}</span> · {followUp.subject}
        </span>
        <span className="shrink-0 font-semibold text-slate-500">{open ? "Hide" : "Read"}</span>
      </button>
      {open && (
        <div className="space-y-2 border-t border-slate-100 px-3 py-2">
          {error ? (
            <p className="text-xs text-red-700">{error}</p>
          ) : !detail ? (
            <p className="flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Opening…</p>
          ) : (
            <>
              <div className="whitespace-pre-wrap break-words text-xs text-slate-800">
                {cleanEmailText(detail.body) || <span className="text-slate-400">No text.</span>}
              </div>
              {detail.attachments.length > 0 && (
                <ul className="space-y-1.5">
                  {detail.attachments.map((file) => (
                    <li key={file.index}>
                      <FileLink
                        href={requestAttachmentUrl(asRequest, file.index)}
                        name={file.filename || "Attachment"}
                        size={readableSize(file.size)}
                      />
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// The whole request email and what came with it, plus anything attached here since.
function RequestDetailDialog({ request, onClose }) {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState("");
  const portalFiles = splitRequestNotes(request.notes).attachments;

  useEffect(() => {
    let current = true;
    fetchRequestDetail(request)
      .then((result) => { if (current) setDetail(result); })
      .catch((err) => { if (current) setError(err.message || "The request couldn't be opened."); });
    return () => { current = false; };
  }, [request]);

  const text = detail ? cleanEmailText(detail.body) : "";

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base">{request.clientName}</DialogTitle>
          <DialogDescription className="text-slate-700">{request.subject}</DialogDescription>
        </DialogHeader>

        <div className="text-xs text-slate-500 space-y-0.5">
          {detail?.from && <p>From: {detail.from}</p>}
          <p>Received: {formatDate(request.date) || "—"}{request.isManual ? " · added by hand" : ""}</p>
        </div>

        {error ? (
          <p className="text-sm text-red-700 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</p>
        ) : !detail ? (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-8 justify-center">
            <Loader2 className="w-4 h-4 animate-spin" /> Opening the request…
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto space-y-4 pr-1">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800 whitespace-pre-wrap break-words">
              {text || <span className="text-slate-400">This request has no text.</span>}
            </div>

            {!request.isManual && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Attachments in the email</p>
                {detail.attachmentsError ? (
                  <p className="text-xs text-amber-800">{detail.attachmentsError}</p>
                ) : detail.attachments.length === 0 ? (
                  <p className="text-xs text-slate-400">None.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {detail.attachments.map((file) => (
                      <li key={file.index}>
                        <FileLink
                          href={requestAttachmentUrl(request, file.index)}
                          name={file.filename || "Attachment"}
                          size={readableSize(file.size)}
                        />
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {request.followUps?.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">
                  Later emails in this thread ({request.followUps.length})
                </p>
                <ul className="space-y-1.5">
                  {request.followUps.map((followUp) => (
                    <li key={followUp.messageId}>
                      <FollowUpEmail followUp={followUp} />
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {portalFiles.length > 0 && (
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Attached in the portal</p>
                <ul className="space-y-1.5">
                  {portalFiles.map((file, i) => (
                    <li key={i}>
                      <FileLink href={file.url} name={file.label} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AddRequestForm({ brand, onAdded }) {
  const [form, setForm] = useState({ clientName: "", aidType: "", dateLodged: "", status: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.clientName.trim()) return toast.error("Enter the client's name.");
    setSaving(true);
    try {
      await createManualRequest({ ...form, clientName: form.clientName.trim() }, brand);
      toast.success(`File request for ${form.clientName.trim()} added.`);
      onAdded();
    } catch (err) {
      toast.error(err.message || "The file request could not be added.");
    } finally {
      setSaving(false);
    }
  };

  const field = "mt-1 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm";
  return (
    <form onSubmit={submit} className="mb-5 grid gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-5">
      <label className="text-xs font-semibold text-slate-600">Client name<input value={form.clientName} onChange={(e) => setForm({ ...form, clientName: e.target.value })} className={field} required /></label>
      <label className="text-xs font-semibold text-slate-600">Aid type<input value={form.aidType} onChange={(e) => setForm({ ...form, aidType: e.target.value })} placeholder="e.g. Summary crime" className={field} /></label>
      <label className="text-xs font-semibold text-slate-600">Date lodged<input type="date" value={form.dateLodged} onChange={(e) => setForm({ ...form, dateLodged: e.target.value })} className={field} /></label>
      <label className="text-xs font-semibold text-slate-600">Status
        <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className={field}>
          {FILE_REQUEST_STATUSES.map((option) => <option key={option.value || "new"} value={option.value}>{option.label}</option>)}
        </select>
      </label>
      <div className="flex items-end">
        <button type="submit" disabled={saving} className="h-9 w-full rounded-lg bg-slate-900 px-4 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60">
          {saving ? "Adding…" : "Add request"}
        </button>
      </div>
      <label className="text-xs font-semibold text-slate-600 sm:col-span-2 lg:col-span-5">Notes<input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={field} /></label>
    </form>
  );
}
